import { stammdatenService } from './StammdatenService.js';
import { zeigeKleineNachricht } from '../systemNachricht.js';

/**
 * Holt Bedarfsdaten für den Bedarfsplaner (Monatsansicht) — siehe reference/bedarfsplaner/BEDARFSPLANER.md.
 */
class BedarfService {
	/**
	 * Holt alle aktiven Bedarfszeilen (Posten x Tag x Zeitfenster) eines Zeitraums der aktuellen
	 * Betriebsstätte, jeweils mit der bereits vom Server live aggregierten Anzahl zugeordneter Schichten
	 * (`filled`).
	 * @param {dayjs} startDatum
	 * @param {dayjs} endDatum
	 * @param {string} [bereichID]
	 * @returns Array von Bedarfszeilen
	 */
	async holeBedarfZeitraum(startDatum, endDatum, bereichID = undefined) {
		try {
			const urlParams = new URLSearchParams({
				StartDatum: startDatum.format('YYYY-MM-DD'),
				EndDatum: endDatum.format('YYYY-MM-DD'),
				BetriebsstaetteID: stammdatenService.aktuelleBetriebsstaette,
			});
			if (bereichID) {
				urlParams.set('BereichID', bereichID);
			}
			const response = await fetch(`/dienstplan/api/bedarf?${urlParams.toString()}`);
			if (!response.ok) {
				return [];
			}
			return await response.json();
		} catch (error) {
			console.log(error);
			return [];
		}
	}

	/**
	 * Legt eine neue Bedarfszeile an.
	 * @param {object} bedarf Bedarfszeile (IDs als Strings, Datum/Zeiten als ISO-Strings)
	 * @returns {Promise<object|false>} die gespeicherte Bedarfszeile oder false bei Fehler
	 */
	async erstelleBedarf(bedarf) {
		return this._sende('/dienstplan/api/bedarf', 'POST', bedarf, 'Speichern fehlgeschlagen!');
	}

	/**
	 * Updatet eine Bedarfszeile; der Server reicht die Änderungen an zugeordnete Schichten weiter.
	 * @returns {Promise<{bedarf: object, schichten: {aktualisiert: number, abweichend: array, fehler: array}}|false>}
	 */
	async updateBedarf(bedarfID, bedarf) {
		return this._sende(`/dienstplan/api/bedarf/${bedarfID}`, 'PUT', bedarf, 'Speichern fehlgeschlagen!');
	}

	/**
	 * Löscht eine Bedarfszeile. Mit Zuordnungen nur mit `schichtenLoeschen` (vom Nutzer bestätigt).
	 * @returns {Promise<boolean>}
	 */
	async entferneBedarf(bedarfID, schichtenLoeschen = false) {
		const ergebnis = await this._sende(`/dienstplan/api/bedarf/${bedarfID}${schichtenLoeschen ? '?schichtenLoeschen=true' : ''}`, 'DELETE', undefined, 'Löschen fehlgeschlagen!');
		return !!ergebnis;
	}

	/**
	 * Kopiert den Bedarf der Vorwoche (ohne Events/Zuordnungen) ergänzend in die Woche ab `montag`.
	 * @param {dayjs} montag Montag der Zielwoche
	 * @returns {Promise<{angelegt: number}|false>}
	 */
	async uebernehmeVorwoche(montag) {
		return this._sende('/dienstplan/api/bedarf/vorwoche-uebernehmen', 'POST', {
			BetriebsstaetteID: stammdatenService.aktuelleBetriebsstaette,
			Montag: montag.format('YYYY-MM-DD'),
		}, 'Übernehmen fehlgeschlagen!');
	}

	async _sende(url, method, body, fehlertext) {
		try {
			const response = await fetch(url, {
				method,
				headers: { 'Content-Type': 'application/json' },
				body: body === undefined ? undefined : JSON.stringify(body),
			});
			if (!response.ok) {
				const message = await response.text();
				zeigeKleineNachricht(message, 0);
				return false;
			}
			return await response.json();
		} catch (error) {
			console.log(error);
			zeigeKleineNachricht(fehlertext, -1);
			return false;
		}
	}

	/**
	 * Holt die kategorisierten Zuordnungs-Kandidaten einer Bedarfszeile (Bedarf-Zuordnung-Dialog).
	 * @param {string} bedarfID
	 * @returns {Promise<object|null>} { Bedarf, Need, Filled, Kandidaten } oder null bei Fehler
	 */
	async holeKandidaten(bedarfID) {
		try {
			const response = await fetch(`/dienstplan/api/bedarf/${bedarfID}/kandidaten`);
			if (!response.ok) {
				const message = await response.text();
				zeigeKleineNachricht(message, 0);
				return null;
			}
			return await response.json();
		} catch (error) {
			console.log(error);
			return null;
		}
	}

	/**
	 * Weist einen Mitarbeiter einer Bedarfszeile zu (erstellt eine Dienstplan-Schicht mit BedarfID).
	 * @returns {Promise<object|false>} die neue Schicht oder false bei Fehler
	 */
	async zuordneMitarbeiter(bedarfID, mitarbeiterID, qualifikationID, qualifikation) {
		try {
			const response = await fetch(`/dienstplan/api/bedarf/${bedarfID}/zuordnung`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ MitarbeiterID: mitarbeiterID, QualifikationID: qualifikationID, Qualifikation: qualifikation }),
			});
			if (!response.ok) {
				const message = await response.text();
				zeigeKleineNachricht(message, 0);
				return false;
			}
			return await response.json();
		} catch (error) {
			console.log(error);
			zeigeKleineNachricht('Zuweisung fehlgeschlagen!', -1);
			return false;
		}
	}

	/**
	 * Entfernt eine Zuordnung, indem die zugehörige Dienstplan-Schicht gelöscht wird.
	 * @returns {Promise<object|false>} die gelöschte Schicht (zum Entfernen ihrer Karte im Raster) oder false
	 */
	async entferneZuordnung(schichtID) {
		try {
			const response = await fetch(`/dienstplan/api/schichten/${schichtID}`, { method: 'DELETE' });
			if (!response.ok) {
				const message = await response.text();
				zeigeKleineNachricht(message, 0);
				return false;
			}
			return await response.json();
		} catch (error) {
			console.log(error);
			zeigeKleineNachricht('Entfernen fehlgeschlagen!', -1);
			return false;
		}
	}

	/**
	 * Setzt eine bestehende Schicht auf eine andere Bedarfszeile um (Umsetzen/Transfer).
	 * @returns {Promise<{quelle: object, ziel: object}|false>} alte (gelöschte) und neue Schicht
	 */
	async umsetzeSchicht(zielBedarfID, schichtID) {
		try {
			const response = await fetch(`/dienstplan/api/bedarf/${zielBedarfID}/umsetzen`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ SchichtID: schichtID }),
			});
			if (!response.ok) {
				const message = await response.text();
				zeigeKleineNachricht(message, 0);
				return false;
			}
			return await response.json();
		} catch (error) {
			console.log(error);
			zeigeKleineNachricht('Umsetzen fehlgeschlagen!', -1);
			return false;
		}
	}
}

const bedarfService = new BedarfService();
export { bedarfService };
